import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
`;

const Container = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 80px;
  align-items: center;
  min-height: 80vh;
  padding: 40px 0;
  max-width: 1400px;
  margin: 0 auto;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -20%;
    width: 140%;
    height: 200%;
    background: 
      radial-gradient(ellipse 60% 80% at 20% 30%, rgba(138, 43, 226, 0.12) 0%, transparent 50%),
      radial-gradient(ellipse 70% 60% at 80% 70%, rgba(59, 130, 246, 0.1) 0%, transparent 55%),
      radial-gradient(ellipse 50% 90% at 50% 50%, rgba(147, 51, 234, 0.08) 0%, transparent 60%);
    filter: blur(80px);
    opacity: 0.6;
    z-index: 0;
    animation: ${pulseGlow} 8s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(221, 214, 254, 0.05) 50%,
      rgba(255, 255, 255, 0.8) 100%
    );
    z-index: 0;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 20px 0;
    min-height: auto;
  }

  @media (max-width: 480px) {
    gap: 32px;
    padding: 16px 0;
  }
`;

const Content = styled.div`
  animation: ${fadeInUp} 0.8s ease-out;
  padding-left: 24px;
  padding-right: 40px;
  max-width: 100%;

  @media (max-width: 900px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const RightContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 24px;
  padding-left: 20px;

  @media (max-width: 900px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 72px;
  line-height: 1.05;
  letter-spacing: -0.04em;
  margin: 0 0 28px;
  font-weight: 700;
  color: #0b0f1a;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 120px;
    height: 2px;
    background: linear-gradient(90deg, rgba(138, 43, 226, 0.6), rgba(59, 130, 246, 0.6));
    animation: ${fadeInUp} 1s ease-out 0.3s both;
  }

  @media (max-width: 900px) {
    font-size: 48px;
    letter-spacing: -0.03em;
    
    &::after {
      width: 80px;
    }
  }

  @media (max-width: 480px) {
    font-size: 36px;
    margin: 0 0 20px;
    
    &::after {
      width: 60px;
    }
  }
`;

const Highlight = styled.span`
  background: linear-gradient(135deg, rgba(138, 43, 226, 0.15), rgba(59, 130, 246, 0.15));
  padding: 4px 12px;
  border-radius: 20px;
  border-bottom-left-radius: 4px;
  font-weight: 700;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    padding: 2px 8px;
    border-radius: 12px;
  }
`;

const SubTitle = styled.p`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 20px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0 0 40px;
  font-weight: 400;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  max-width: 580px;

  @media (max-width: 900px) {
    font-size: 18px;
    margin-bottom: 32px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 24px;
    line-height: 1.5;
  }
`;

const CTA = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  padding: 0 32px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #0b0f1a 0%, #1a1f2e 100%);
  color: white;
  text-decoration: none;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 17px;
  box-shadow: 0 4px 16px rgba(11, 15, 26, 0.2), 0 0 0 1px rgba(221, 214, 254, 0.1);
  position: relative;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 100%;
    height: 48px;
    font-size: 15px;
    padding: 0 24px;
  }
`;


const SecondaryButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  padding: 0 32px;
  background: #ddd6fe;
  color: #0b0f1a;
  text-decoration: none;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 17px;
  border-radius: 14px;
  border: 2px solid #ddd6fe;
  white-space: nowrap;

  @media (max-width: 480px) {
    width: 100%;
    height: 48px;
    font-size: 15px;
    padding: 0 24px;
  }
`;

const LimitedTimeBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  padding: 4px 8px;
  background: #0b0f1a;
  color: #ffffff;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  z-index: 2;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transform: rotate(5deg);
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    right: -2px;
    bottom: -2px;
    left: -2px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
    z-index: -1;
    filter: blur(4px);
  }
`;

const SocialProof = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  animation: ${fadeInUp} 0.8s ease-out 0.5s both;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-top: 24px;
  }
`;

const AvatarGroup = styled.div`
  display: flex;
  align-items: center;
`;

const Avatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  margin-left: -6px;
  object-fit: cover;
  
  &:first-of-type {
    margin-left: 0;
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    margin-left: -5px;
  }
`;

const SocialText = styled.div`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid rgba(221, 214, 254, 0.2);
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;

  @media (max-width: 600px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 24px;
    margin-top: 32px;
    padding-top: 24px;
  }

  @media (max-width: 480px) {
    gap: 16px;
    margin-top: 24px;
    padding-top: 20px;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 600px) {
    flex: 1 1 calc(50% - 12px);
    min-width: 0;
  }

  @media (max-width: 480px) {
    flex: 1 1 calc(33% - 12px);
    gap: 2px;
  }
`;

const StatValue = styled.div`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #0b0f1a;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const StatLabel = styled.div`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

interface HeroSectionProps {
  children?: React.ReactNode;
}

const HeroSection: React.FC<HeroSectionProps> = ({ children }) => {
  return (
    <Container>
      <Content>
        <Title>
          Let your website <Highlight>talk back</Highlight>, smartly.
        </Title>
        <SubTitle>
          Build AI-powered support agents that understand your business, answer questions instantly, and turn visitors into customers—all without writing a single line of code.
        </SubTitle>
        <CTA>
          <Button href="/get-started">Get Started</Button>
          <SecondaryButtonWrapper>
            <LimitedTimeBadge>Limited Time</LimitedTimeBadge>
            <SecondaryButton
              href="#lifetime-deal"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('lifetime-deal')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              Lifetime Deal
            </SecondaryButton>
          </SecondaryButtonWrapper>
        </CTA>

        <SocialProof>
          <AvatarGroup>
            {['Favicon-100x100.webp', 'favicon.jpg', 'favicon-32x32.png', 'favicon-32x32 (1).png', 'faviconV2.png', 'background-32x32.webp'].map((logo, i) => (
              <Avatar key={i} src={`/Clients/${logo}`} alt={`Client ${i + 1}`} />
            ))}
          </AvatarGroup>
          <SocialText>
            <strong style={{ color: '#0b0f1a' }}>6 handpicked partners</strong> are live. <strong style={{ color: '#0b0f1a' }}>4 seats remain</strong> before launch
          </SocialText>
        </SocialProof>
        <StatsContainer>
          <StatItem>
            <StatValue>80%</StatValue>
            <StatLabel>Queries resolved automatically</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>24/7</StatValue>
            <StatLabel>Always available support</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>5 min</StatValue>
            <StatLabel>Setup time</StatLabel>
          </StatItem>
        </StatsContainer>
      </Content>
      <RightContent>
        {children}
      </RightContent>
    </Container>
  );
};

export default HeroSection;